"use client";

import { HandyoutPaletteScope } from "@/components/handyout-palette-scope";
import Device from "@/components/device";
import { Button } from "@/components/ui/button";
import { DEFAULT_PRIMARY } from "@/lib/handyout-palette";
import { PageModule } from "@/types/modules";
import {
  motion,
  MotionValue,
  useMotionTemplate,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ReactNode, useRef } from "react";

interface HeroCta {
  label: string;
  href: string;
}

interface HeroIndustriesProps {
  bgImageSrc: string;
  heading: string;
  subheading?: string;
  paragraph?: string;
  ctaPrimary?: HeroCta;
  ctaSecondary?: HeroCta;
  phonePage: PageModule;
  scrollToExpand?: string;
  primaryColor?: string;
  children?: ReactNode;
}

function useReveal(progress: MotionValue<number>, start: number, end: number) {
  const y = useTransform(progress, [start, end], [48, 0]);
  const opacity = useTransform(progress, [start, end], [0, 1]);
  const blurPx = useTransform(progress, [start, end], [14, 0]);
  const filter = useMotionTemplate`blur(${blurPx}px)`;
  return { y, opacity, filter };
}

const HeroIndustries = ({
  bgImageSrc,
  heading,
  subheading,
  paragraph,
  ctaPrimary,
  ctaSecondary,
  phonePage,
  scrollToExpand,
  primaryColor,
  children,
}: HeroIndustriesProps) => {
  const containerRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 26,
    mass: 0.7,
  });

  const phoneX = useTransform(progress, [0, 1], [0, 380]);
  const phoneScale = useTransform(progress, [0, 1], [1, 0.92]);

  const bgScale = useTransform(progress, [0, 1], [1, 0.88]);
  const bgRadiusPx = useTransform(progress, [0, 1], [0, 24]);
  const bgBorderRadius = useMotionTemplate`${bgRadiusPx}px`;

  const headingReveal = useReveal(progress, 0.15, 0.35);
  const subheadingReveal = useReveal(progress, 0.3, 0.5);
  const paragraphReveal = useReveal(progress, 0.45, 0.65);
  const buttonsReveal = useReveal(progress, 0.6, 0.8);
  const scrollHintOpacity = useTransform(progress, [0, 0.15], [1, 0]);

  return (
    <>
      <section
        ref={containerRef}
        className="relative w-full"
        style={{ height: "220vh" }}
      >
        <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center">
          <motion.div
            className="absolute inset-0 z-0 overflow-hidden"
            style={{ scale: bgScale, borderRadius: bgBorderRadius }}
          >
            <Image
              src={bgImageSrc}
              unoptimized
              alt="Background"
              fill
              className="object-cover"
              sizes="(max-aspect-ratio: 4/3) 134vh, 100vw"
              priority
            />
            <div className="absolute inset-0 bg-black/25" />
          </motion.div>

          <div className="relative z-10 mx-auto w-full max-w-[80rem] px-4 sm:px-8 h-full flex items-center">
            <div className="w-full max-w-xl">
              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-tight"
                style={headingReveal}
              >
                {heading}
              </motion.h1>
              {subheading && (
                <motion.h2
                  className="text-xl md:text-2xl text-white/85 font-medium mt-4"
                  style={subheadingReveal}
                >
                  {subheading}
                </motion.h2>
              )}
              {paragraph && (
                <motion.p
                  className="text-base md:text-lg text-white/75 leading-relaxed mt-4"
                  style={paragraphReveal}
                >
                  {paragraph}
                </motion.p>
              )}
              {(ctaPrimary || ctaSecondary) && (
                <motion.div
                  className="flex flex-row flex-wrap items-center gap-3 mt-6"
                  style={buttonsReveal}
                >
                  {ctaPrimary && (
                    <Button asChild size="sm">
                      <Link href={ctaPrimary.href}>{ctaPrimary.label}</Link>
                    </Button>
                  )}
                  {ctaSecondary && (
                    <Button asChild size="sm" variant="outline">
                      <Link href={ctaSecondary.href}>{ctaSecondary.label}</Link>
                    </Button>
                  )}
                </motion.div>
              )}
            </div>

            <motion.div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
              style={{ x: phoneX, scale: phoneScale }}
            >
              <HandyoutPaletteScope
                primaryColor={primaryColor ?? DEFAULT_PRIMARY}
                scopeId="hero-industries"
                className="w-87 h-175"
              >
                <div className="size-full scale-75 md:scale-none">
                  <Device
                    pages={[phonePage]}
                    showDeviceMockup
                    splitting={false}
                    mode="interactive"
                  />
                </div>
              </HandyoutPaletteScope>
            </motion.div>

            {scrollToExpand && (
              <motion.p
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-sm text-white/80 z-20 pointer-events-none"
                style={{ opacity: scrollHintOpacity }}
              >
                {scrollToExpand}
              </motion.p>
            )}
          </div>
        </div>
      </section>

      {children && (
        <section className="flex flex-col w-full px-8 py-10 md:px-16 lg:py-20">
          {children}
        </section>
      )}
    </>
  );
};

export default HeroIndustries;
